import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import VehicleCard from "./VehicleCard";
import VehicleModal from "./VehicleModal";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "@/contexts/ThemeContext";

export default function FeaturedVehicles({ vehicles, loading }) {
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { t } = useTranslation();
  const { isDark } = useTheme();

  const openModal = (vehicle) => {
    setSelectedVehicle(vehicle);
    setModalOpen(true);
  };

  return (
    <section 
      className={`py-24 md:py-32 transition-colors duration-300 ${isDark ? 'bg-[#050505]' : 'bg-[#FAFAFA]'}`} 
      data-testid="featured-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between mb-12"
        >
          <div>
            <span className={`text-sm uppercase tracking-widest font-medium mb-3 block ${
              isDark ? 'text-blue-500' : 'text-red-600'
            }`}>
              {t('featured.title')} {t('featured.titleAccent')}
            </span>
            <h2 className={`font-unbounded text-3xl md:text-4xl font-bold ${
              isDark ? 'text-white' : 'text-gray-900'
            }`}>
              {t('featured.description')}
            </h2>
            <p className={`mt-3 max-w-md ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {t('featured.description')}
            </p>
          </div>
          <Link to="/inventory" className="mt-6 md:mt-0">
            <Button
              variant="outline"
              className={`rounded-none px-6 py-3 font-semibold text-sm uppercase tracking-wider group ${
                isDark 
                  ? 'border-white/20 text-white hover:bg-white/10' 
                  : 'border-gray-300 text-gray-900 hover:bg-gray-100 hover:text-gray-900'
              }`}
              data-testid="view-all-inventory-btn"
            >
              {t('featured.viewAll')}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </Link>
        </motion.div>

        {/* Vehicle Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, i) => (
              <div key={i} className={`border animate-pulse ${
                isDark 
                  ? 'bg-[#0A0A0A] border-white/10' 
                  : 'bg-white border-gray-200 shadow-sm'
              }`}>
                <div className={`aspect-[16/10] ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-100'}`} />
                <div className="p-6 space-y-3">
                  <div className={`h-4 rounded w-1/3 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                  <div className={`h-6 rounded w-2/3 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                  <div className={`h-4 rounded w-1/2 ${isDark ? 'bg-[#1A1A1A]' : 'bg-gray-200'}`} />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(Array.isArray(vehicles) ? vehicles : []).slice(0, 6).map((vehicle, index) => (
              <motion.div
                key={vehicle.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <VehicleCard
                  vehicle={vehicle}
                  onClick={() => openModal(vehicle)}
                />
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Vehicle Modal */}
      <VehicleModal
        vehicle={selectedVehicle}
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedVehicle(null);
        }}
      />
    </section>
  );
}
